import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import FormularioTexto, { FormularioTextoRef } from './FormularioTexto';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Chip,
  Avatar,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
  Collapse,
  Stack,
  Paper,
  keyframes,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Fade
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  Psychology as PsychologyIcon,
  Assignment as AssignmentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  ArrowBack as ArrowBackIcon,
  Stop as StopIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { evolucaoService, clienteService } from '../../services/api';

// Configuração da API
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://trajetus.net';

// =====================================================
// ANIMAÇÕES
// =====================================================
const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const PulsingStopIcon = styled(StopIcon)(({ theme }) => ({
  animation: `${pulseAnimation} 1.5s ease-in-out infinite`,
  color: theme.palette.error.main,
}));

// =====================================================
// INTERFACES
// =====================================================

interface Evolucao {
  id: number;
  id_cliente: number;
  cliente_nome: string;
  tipo_sessao?: string;
  procedimento: string;
  sintese: string;
  conduta: string;
  observacoes?: string;
  data_cadastro: string;
  ultima_atualizacao?: string;
  id_usuario: number;
}

interface EvolucaoFormData {
  id_cliente: number | string;
  tipo_sessao: string;
  procedimento: string;
  sintese: string;
  conduta: string;
  observacoes: string;
  data_cadastro: string;
  id_usuario?: number;
}

interface Cliente {
  id: number;
  nome: string;
  ativo?: boolean | number;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

// =====================================================
// FUNÇÕES AUXILIARES
// =====================================================

const formatarDataSemFusoHorario = (dataString: string): string => {
  if (!dataString) return 'Data não informada';
  
  // Se a data já está no formato YYYY-MM-DD, criar a data diretamente
  const [ano, mes, dia] = dataString.split('-');
  if (ano && mes && dia) {
    const data = new Date(parseInt(ano), parseInt(mes) - 1, parseInt(dia));
    return data.toLocaleDateString('pt-BR');
  }
  
  // Fallback para outros formatos
  return new Date(dataString).toLocaleDateString('pt-BR');
};

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const Evolucoes: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // =====================================================
  // ESTADOS
  // =====================================================
  const [evolucoes, setEvolucoes] = useState<Evolucao[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  
  // Estados para filtros avançados
  const [showFilters, setShowFilters] = useState(false);
  const [filtros, setFiltros] = useState({
    cliente_id: '',
    data_inicio: '',
    data_fim: ''
  });
  const [filtrosAplicados, setFiltrosAplicados] = useState({
    cliente_id: '',
    data_inicio: '',
    data_fim: ''
  });
  const [showForm, setShowForm] = useState<boolean>(false);
  const [formData, setFormData] = useState<EvolucaoFormData>({
    id_cliente: 0,
    tipo_sessao: '',
    procedimento: '',
    sintese: '',
    conduta: '',
    observacoes: '',
    data_cadastro: new Date().toISOString().split('T')[0],
    id_usuario: user?.id || 0
  });

  // Estados simples e diretos para os campos de texto
  const [procedimentoValue, setProcedimentoValue] = useState('');
  const [sinteseValue, setSinteseValue] = useState('');
  const [condutaValue, setCondutaValue] = useState('');
  const [observacoesValue, setObservacoesValue] = useState('');








  const [isListening, setIsListening] = useState<{[key: string]: boolean}>({
    procedimento: false,
    sintese: false,
    conduta: false
  });
  const [voiceModal, setVoiceModal] = useState({
    open: false,
    field: '',
    text: '',
    isListening: false
  });
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });

  // =====================================================
  // EFEITOS
  // =====================================================
  useEffect(() => {
    const carregarDadosIniciais = async () => {
      await carregarClientes();
      await carregarEvolucoes();
    };
    carregarDadosIniciais();
  }, []);

  useEffect(() => {
    return () => {
      // Parar qualquer áudio que esteja tocando ao sair da tela
      speechSynthesis.cancel();
      
      // Parar reconhecimento de voz se estiver ativo
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  // =====================================================
  // FUNÇÕES DE API
  // =====================================================
  const carregarEvolucoes = async (filtrosPersonalizados?: any): Promise<void> => {
    try {
      setLoading(true);
      
      if (!user?.id) {
        console.log('🔍 [DEBUG] Usuário não logado, não carregando evoluções');
        setEvolucoes([]);
        showSnackbar('Usuário não identificado!', 'warning');
        return;
      }
      
      let response;
      const filtrosParaUsar = filtrosPersonalizados || filtrosAplicados;
      if (filtrosParaUsar && (filtrosParaUsar.cliente_id || filtrosParaUsar.data_inicio || filtrosParaUsar.data_fim)) {
        const filtrosComUsuario = { ...filtrosParaUsar, id_usuario: user.id };
        response = await evolucaoService.listar(filtrosComUsuario);
      } else {
        response = await evolucaoService.buscarPorUsuario(user.id);
      }
      
      console.log('🔍 [DEBUG] Resposta completa da API:', response);
      console.log('🔍 [DEBUG] response.data:', response.data);
      console.log('🔍 [DEBUG] response.data.data:', response.data?.data);

      let evolucoesList: any[] = [];
      if (response.data) {
        if (Array.isArray(response.data.data)) {
          evolucoesList = response.data.data;
        } else if (Array.isArray(response.data)) {
          evolucoesList = response.data;
        }
      }

      console.log('🔍 [DEBUG] evolucoesList bruto:', evolucoesList);

      let clientesAtuais = clientes;
      if (!clientesAtuais || clientesAtuais.length === 0) {
        clientesAtuais = await carregarClientes();
      }

      const evolucoesComNome = evolucoesList.map((evolucao: any) => {
        const clienteRelacionado = clientesAtuais.find(
          (c: any) => Number(c.id) === Number(evolucao.id_cliente)
        );
        const nomeCliente =
          clienteRelacionado?.nome ||
          evolucao.cliente_nome ||
          'Cliente não informado';

        return {
          ...evolucao,
          cliente_nome: nomeCliente,
        };
      });

      console.log('🔍 [DEBUG] evolucoesComNome final:', evolucoesComNome);
      console.log(
        '🔍 [DEBUG] Evoluções carregadas para usuário',
        user.id,
        ':',
        evolucoesComNome.length
      );
      
      setEvolucoes(evolucoesComNome as Evolucao[]);
      showSnackbar(`${evolucoesComNome.length} evoluções carregadas!`, 'success');
    } catch (error) {
      console.error('Erro ao carregar evoluções:', error);
      showSnackbar('Erro ao carregar evoluções. Tente novamente.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const carregarClientes = async (): Promise<Cliente[]> => {
    try {
      console.log('🔍 [DEBUG] Iniciando carregamento de clientes...');
      
      if (!user?.id) {
        console.log('🔍 [DEBUG] Usuário não logado, não carregando clientes');
        setClientes([]);
        showSnackbar('Usuário não identificado!', 'warning');
        return [];
      }
      
      const response = await clienteService.listarPorUsuario(user.id);
      console.log('🔍 [DEBUG] Resposta completa da API:', response);
      
      // O clienteService retorna response.data
      const data = response.data;
      console.log('🔍 [DEBUG] Dados recebidos da API:', data);
      
      // Garantir que sempre seja um array
      let clientesArray: Cliente[] = [];
      if (Array.isArray(data)) {
        clientesArray = data;
      } else if (data && Array.isArray(data.data)) {
        clientesArray = data.data;
      } else {
        console.warn('⚠️ [DEBUG] Dados de clientes não são array:', data);
        clientesArray = [];
      }
      
      console.log('🔍 [DEBUG] Clientes carregados para usuário', user.id, ':', clientesArray.length);
      setClientes(clientesArray);
      return clientesArray;
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      setClientes([]);
      showSnackbar('Erro ao carregar lista de clientes.', 'error');
      return [];
    }
  };

  // =====================================================
  // FUNÇÕES DE MANIPULAÇÃO
  // =====================================================
  const showSnackbar = (message: string, severity: SnackbarState['severity']): void => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (): void => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleNew = (): void => {
    setFormData({
      id_cliente: '', // Mudado de 0 para string vazia para evitar warning do Select
      tipo_sessao: '',
      procedimento: '',
      sintese: '',
      conduta: '',
      observacoes: '',
      data_cadastro: new Date().toISOString().split('T')[0],
      id_usuario: user?.id || 0
    });
    // Limpar estados separados
    setProcedimentoValue('');
    setSinteseValue('');
    setCondutaValue('');
    setObservacoesValue('');
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (evolucao: Evolucao) => {
    console.log('🔍 [DEBUG] Editando evolução:', evolucao);
    console.log('🔍 [DEBUG] Data original:', evolucao.data_cadastro);
    
    setEditingId(evolucao.id);
    
    // O backend agora retorna a data no formato correto (YYYY-MM-DD)
    const dataFormatada = evolucao.data_cadastro || new Date().toISOString().split('T')[0];
    
    console.log('🔍 [DEBUG] Data formatada:', dataFormatada);
    
    setFormData({
      id_cliente: evolucao.id_cliente,
      tipo_sessao: evolucao.tipo_sessao || '',
      procedimento: evolucao.procedimento,
      sintese: evolucao.sintese,
      conduta: evolucao.conduta,
      observacoes: evolucao.observacoes || '',
      data_cadastro: dataFormatada,
      id_usuario: evolucao.id_usuario
    });
    // Sincronizar estados separados
    setProcedimentoValue(evolucao.procedimento || '');
    setSinteseValue(evolucao.sintese || '');
    setCondutaValue(evolucao.conduta || '');
    setObservacoesValue(evolucao.observacoes || '');
    setShowForm(true);
  };

  const handleCancel = (): void => {
    // Parar qualquer áudio que esteja tocando ao cancelar
    speechSynthesis.cancel();
    
    // Parar reconhecimento de voz se estiver ativo
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    
    setShowForm(false);
    setEditingId(null);
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    // Obter valores do formulário isolado
    const valoresFormulario = formularioTextoRef.current?.getValues();
    if (!valoresFormulario) {
      showSnackbar('Erro ao acessar os dados do formulário.', 'error');
      return;
    }
    
    if (!formData.id_cliente || formData.id_cliente === '' || !valoresFormulario.procedimento || !valoresFormulario.sintese || !valoresFormulario.conduta) {
      showSnackbar('Preencha todos os campos obrigatórios.', 'warning');
      return;
    }

    try {
      setSaving(true);
      
      const dadosParaSalvar = {
        ...formData,
        procedimento: valoresFormulario.procedimento,
        sintese: valoresFormulario.sintese,
        conduta: valoresFormulario.conduta,
        observacoes: valoresFormulario.observacoes,
        id_usuario: user?.id || 0
      };
      
      if (editingId) {
        await evolucaoService.atualizar(editingId, dadosParaSalvar);
        showSnackbar('Evolução atualizada com sucesso!', 'success');
      } else {
        await evolucaoService.criar(dadosParaSalvar);
        showSnackbar('Evolução criada com sucesso!', 'success');
      }
      
      await carregarEvolucoes();
      setShowForm(false);
      setEditingId(null);
      
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      showSnackbar(error.message || 'Erro ao salvar evolução.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number): Promise<void> => {
    if (!window.confirm('Tem certeza que deseja excluir esta evolução?')) {
      return;
    }

    try {
      setLoading(true);
      await evolucaoService.excluir(id);
      showSnackbar('Evolução excluída com sucesso!', 'success');
      await carregarEvolucoes();
    } catch (error: any) {
      console.error('Erro ao excluir:', error);
      showSnackbar(error.message || 'Erro ao excluir evolução.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id: number): void => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  // Função para reconhecimento de voz
  // Armazenar referência para o reconhecimento ativo
  const recognitionRef = React.useRef<any>(null);
  const formularioTextoRef = useRef<FormularioTextoRef>(null);



  const startListening = (fieldName: string): void => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      showSnackbar('Reconhecimento de voz não suportado neste navegador.', 'warning');
      return;
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.lang = 'pt-BR';
    recognition.continuous = true;
    recognition.interimResults = true;

    // Armazenar referência para poder parar depois
    recognitionRef.current = recognition;
    setIsListening(prev => ({ ...prev, [fieldName]: true }));

    // Armazenar o valor inicial do campo para concatenar
    const initialValue = formData[fieldName as keyof EvolucaoFormData] as string;

    recognition.onresult = (event: any) => {
      let finalTranscript = '';
      let interimTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript;
        } else {
          interimTranscript += event.results[i][0].transcript;
        }
      }
      
      // Atualizar o campo em tempo real com texto final + interim
      const currentText = initialValue + (finalTranscript ? ' ' + finalTranscript : '') + (interimTranscript ? ' ' + interimTranscript : '');
      setFormData(prev => ({
        ...prev,
        [fieldName]: currentText.trim()
      }));
    };

    recognition.onerror = (event: any) => {
      console.error('Erro no reconhecimento de voz:', event.error);
      setIsListening(prev => ({ ...prev, [fieldName]: false }));
      recognitionRef.current = null;
      showSnackbar('Erro no reconhecimento de voz. Tente novamente.', 'error');
    };

    recognition.onend = () => {
      setIsListening(prev => ({ ...prev, [fieldName]: false }));
      recognitionRef.current = null;
    };

    recognition.start();
  };

  const stopListening = (fieldName: string): void => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(prev => ({ ...prev, [fieldName]: false }));
  };

  // Funções para o modal de voz
  const openVoiceModal = (field: string) => {
    let currentValue = '';
    if (field === 'procedimento') currentValue = procedimentoValue;
    else if (field === 'sintese') currentValue = sinteseValue;
    else if (field === 'conduta') currentValue = condutaValue;
    else if (field === 'observacoes') currentValue = observacoesValue;
    
    setVoiceModal({
      open: true,
      field,
      text: currentValue,
      isListening: false
    });
  };

  const closeVoiceModal = () => {
    if (voiceModal.isListening && recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setVoiceModal({
      open: false,
      field: '',
      text: '',
      isListening: false
    });
  };

  const startModalListening = () => {
    if (!('webkitSpeechRecognition' in window)) {
      setSnackbar({
        open: true,
        message: 'Seu navegador não suporta reconhecimento de voz',
        severity: 'error'
      });
      return;
    }

    const recognition = new (window as any).webkitSpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'pt-BR';

    let finalTranscript = '';

    recognition.onresult = (event: any) => {
      let interimTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }

      const newText = voiceModal.text + finalTranscript + interimTranscript;
      setVoiceModal(prev => ({ ...prev, text: newText }));
    };

    recognition.onerror = (event: any) => {
      console.error('Erro no reconhecimento de voz:', event.error);
      setVoiceModal(prev => ({ ...prev, isListening: false }));
    };

    recognition.onend = () => {
      setVoiceModal(prev => ({ ...prev, isListening: false }));
    };

    recognitionRef.current = recognition;
    recognition.start();
    setVoiceModal(prev => ({ ...prev, isListening: true }));
  };

  const stopModalListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setVoiceModal(prev => ({ ...prev, isListening: false }));
    }
  };

  const importVoiceText = () => {
    // Usar estados separados
    if (voiceModal.field === 'procedimento') {
      setProcedimentoValue(voiceModal.text);
    } else if (voiceModal.field === 'sintese') {
      setSinteseValue(voiceModal.text);
    } else if (voiceModal.field === 'conduta') {
      setCondutaValue(voiceModal.text);
    } else if (voiceModal.field === 'observacoes') {
      setObservacoesValue(voiceModal.text);
    }
    closeVoiceModal();
  };

  // Funções para o modal de filtros
  const handleOpenFilters = () => {
    setFiltros({ ...filtrosAplicados });
    setShowFilters(true);
  };

  const handleCloseFilters = () => {
    setShowFilters(false);
  };

  const handleApplyFilters = async () => {
    setFiltrosAplicados({ ...filtros });
    setShowFilters(false);
    await carregarEvolucoes(filtros);
  };

  const handleClearFilters = async () => {
    const filtrosLimpos = {
      cliente_id: '',
      data_inicio: '',
      data_fim: ''
    };
    setFiltros(filtrosLimpos);
    setFiltrosAplicados(filtrosLimpos);
    setShowFilters(false);
    await carregarEvolucoes(filtrosLimpos);
  };

  // Filtrar evoluções
  // Filtrar evoluções com verificação de segurança
const evolucoesFiltradas = Array.isArray(evolucoes) ? evolucoes.filter(evolucao =>
  evolucao.cliente_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  evolucao.procedimento?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  evolucao.sintese?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  evolucao.conduta?.toLowerCase().includes(searchTerm.toLowerCase())
) : [];

  // Função para imprimir evolução específica
  const handlePrintEvolucao = async (evolucao: Evolucao) => {
    try {
      // Buscar dados do usuário
      const responseUsuario = await fetch(`${API_BASE_URL}/api/usuarios/${evolucao.id_usuario}`);
      const userDataResponse = await responseUsuario.json();
      const usuario = userDataResponse.data || userDataResponse;

      // Buscar dados do cliente
      const responseCliente = await fetch(`${API_BASE_URL}/api/clientes/${evolucao.id_cliente}`);
      const clienteDataResponse = await responseCliente.json();
      const cliente = clienteDataResponse.data || clienteDataResponse;

      const printContent = `
        <!DOCTYPE html>
        <html lang="pt-BR">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Evolução - ${evolucao.cliente_nome}</title>
          <style>
            @media print {
              .no-print { display: none !important; }
            }
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              line-height: 1.6;
              color: #333;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #4caf50;
              padding-bottom: 15px;
              margin-bottom: 20px;
            }
            .header h1 {
              color: #4caf50;
              margin: 0;
              font-size: 28px;
            }
            .info-section {
              display: flex;
              justify-content: space-between;
              margin-bottom: 20px;
              padding: 12px;
              background-color: #f8f9fa;
              border-radius: 8px;
            }
            .info-item {
              flex: 1;
            }
            .info-label {
              font-weight: bold;
              color: #4caf50;
              margin-bottom: 3px;
            }
            .content-section {
              margin-bottom: 15px;
              padding: 15px;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
            }
            .content-title {
              font-size: 16px;
              font-weight: bold;
              color: #4caf50;
              margin-bottom: 10px;
              border-bottom: 1px solid #e0e0e0;
              padding-bottom: 5px;
            }
            .content-text {
              font-size: 14px;
              line-height: 1.6;
              text-align: justify;
            }
            .signature-section {
              margin-top: 25px;
              padding: 15px;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              background-color: #f8f9fa;
            }
            .signature-title {
              font-size: 16px;
              font-weight: bold;
              color: #4caf50;
              margin-bottom: 10px;
            }
            .signature-info {
              margin-bottom: 2px;
              line-height: 1.1;
            }
            .signature-name {
              font-weight: bold;
              font-size: 16px;
              margin-bottom: 2px;
              line-height: 1.1;
            }
            .signature-cpf-crp {
              font-size: 12px;
              color: #666;
            }
            .signature-line {
              border-top: 1px solid #333;
              width: 300px;
              margin-bottom: 15px;
              padding-top: 5px;
              text-align: center;
              font-size: 12px;
            }
            .signature-image {
              max-width: 200px;
              max-height: 60px;
              margin-bottom: 5px;
              border: 1px solid #ddd;
            }
            .footer {
              margin-top: 25px;
              text-align: center;
              font-size: 12px;
              color: #666;
              border-top: 1px solid #e0e0e0;
              padding-top: 15px;
              line-height: 1.0;
            }
            .print-button {
              position: fixed;
              top: 20px;
              right: 20px;
              background: #4caf50;
              color: white;
              border: none;
              padding: 12px 20px;
              border-radius: 25px;
              cursor: pointer;
              font-size: 14px;
              box-shadow: 0 4px 8px rgba(0,0,0,0.2);
              z-index: 1000;
            }
            .print-button:hover {
              background: #45a049;
            }
          </style>
        </head>
        <body>
          <button class="print-button no-print" onclick="window.print()">
            🖨️ Imprimir
          </button>
          
          <div class="header">
            <h1>EVOLUÇÃO CLÍNICA</h1>
          </div>

          <div class="info-section">
            <div class="info-item">
              <div class="info-label">Cliente:</div>
              <div>${evolucao.cliente_nome}</div>
              <div class="signature-cpf-crp">CPF: ${cliente.cpf || 'Não informado'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Data Cadastro:</div>
              <div>${formatarDataSemFusoHorario(evolucao.data_cadastro)}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Última Atualização:</div>
              <div>${evolucao.ultima_atualizacao ? formatarDataSemFusoHorario(evolucao.ultima_atualizacao) : 'Não informado'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Registro:</div>
              <div>#${evolucao.id}</div>
            </div>
          </div>

          <div class="content-section">
            <div class="content-title">📋 Procedimento</div>
            <div class="content-text">${evolucao.procedimento}</div>
          </div>

          <div class="content-section">
            <div class="content-title">📝 Síntese</div>
            <div class="content-text">${evolucao.sintese || 'Não informado'}</div>
          </div>

          <div class="content-section">
            <div class="content-title">🎯 Conduta</div>
            <div class="content-text">${evolucao.conduta || 'Não informado'}</div>
          </div>

          <div class="signature-section">
            <div class="signature-title">👨‍⚕️ Psicólogo(a) Responsável</div>
            ${usuario.assinatura && usuario.assinatura.startsWith ? `
              <img src="${usuario.assinatura.startsWith('data:') ? usuario.assinatura : 'data:image/png;base64,' + usuario.assinatura}" alt="Assinatura" class="signature-image" />
            ` : `
              <div class="signature-line">Assinatura</div>
            `}
            <div class="signature-name">${usuario.user_name || 'Nome não informado'}</div>
            <div class="signature-info signature-cpf-crp">CPF: ${usuario.cpf || 'Não informado'}</div>
            <div class="signature-info signature-cpf-crp">CRP: ${usuario.crp || 'Não informado'}</div>
          </div>

          <div class="footer">
            <p>Documento gerado em ${new Date().toLocaleString('pt-BR')}</p>
            <p>Trajetus - Sistema de Gestão Psicológica</p>
          </div>
        </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();
      }
    } catch (error) {
      console.error('Erro ao buscar dados do usuário/cliente:', error);
      // Fallback sem dados do usuário/cliente
      const printContent = `
        <!DOCTYPE html>
        <html lang="pt-BR">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Evolução - ${evolucao.cliente_nome}</title>
          <style>
            @media print {
              .no-print { display: none !important; }
            }
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              line-height: 1.6;
              color: #333;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #4caf50;
              padding-bottom: 15px;
              margin-bottom: 20px;
            }
            .header h1 {
              color: #4caf50;
              margin: 0;
              font-size: 28px;
            }
            .info-section {
              display: flex;
              justify-content: space-between;
              margin-bottom: 20px;
              padding: 12px;
              background-color: #f8f9fa;
              border-radius: 8px;
            }
            .info-item {
              flex: 1;
            }
            .info-label {
              font-weight: bold;
              color: #4caf50;
              margin-bottom: 3px;
            }
            .content-section {
              margin-bottom: 15px;
              padding: 15px;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
            }
            .content-title {
              font-size: 16px;
              font-weight: bold;
              color: #4caf50;
              margin-bottom: 10px;
              border-bottom: 1px solid #e0e0e0;
              padding-bottom: 5px;
            }
            .content-text {
              font-size: 14px;
              line-height: 1.6;
              text-align: justify;
            }
            .signature-section {
              margin-top: 25px;
              padding: 15px;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              background-color: #f8f9fa;
            }
            .signature-title {
              font-size: 16px;
              font-weight: bold;
              color: #4caf50;
              margin-bottom: 10px;
            }
            .signature-line {
              border-top: 1px solid #333;
              width: 300px;
              margin-bottom: 15px;
              padding-top: 5px;
              text-align: center;
              font-size: 12px;
            }
            .signature-image {
              max-width: 200px;
              max-height: 60px;
              margin-bottom: 5px;
              border: 1px solid #ddd;
            }
            .footer {
              margin-top: 25px;
              text-align: center;
              font-size: 12px;
              color: #666;
              border-top: 1px solid #e0e0e0;
              padding-top: 15px;
              line-height: 1.0;
            }
            .print-button {
              position: fixed;
              top: 20px;
              right: 20px;
              background: #4caf50;
              color: white;
              border: none;
              padding: 12px 20px;
              border-radius: 25px;
              cursor: pointer;
              font-size: 14px;
              box-shadow: 0 4px 8px rgba(0,0,0,0.2);
              z-index: 1000;
            }
            .print-button:hover {
              background: #45a049;
            }
          </style>
        </head>
        <body>
          <button class="print-button no-print" onclick="window.print()">
            🖨️ Imprimir
          </button>
          
          <div class="header">
            <h1>EVOLUÇÃO CLÍNICA</h1>
          </div>

          <div class="info-section">
            <div class="info-item">
              <div class="info-label">Cliente:</div>
              <div>${evolucao.cliente_nome}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Data Cadastro:</div>
              <div>${new Date(evolucao.data_cadastro).toLocaleDateString('pt-BR')}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Última Atualização:</div>
              <div>${evolucao.ultima_atualizacao ? new Date(evolucao.ultima_atualizacao).toLocaleDateString('pt-BR') : 'Não informado'}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Registro:</div>
              <div>#${evolucao.id}</div>
            </div>
          </div>

          <div class="content-section">
            <div class="content-title">📋 Procedimento</div>
            <div class="content-text">${evolucao.procedimento}</div>
          </div>

          <div class="content-section">
            <div class="content-title">📝 Síntese</div>
            <div class="content-text">${evolucao.sintese || 'Não informado'}</div>
          </div>

          <div class="content-section">
            <div class="content-title">🎯 Conduta</div>
            <div class="content-text">${evolucao.conduta || 'Não informado'}</div>
          </div>

          <div class="signature-section">
            <div class="signature-title">👨‍⚕️ Psicólogo(a) Responsável</div>
            <div class="signature-line">Assinatura</div>
          </div>

          <div class="footer">
            <p>Documento gerado em ${new Date().toLocaleString('pt-BR')}</p>
            <p>Trajetus - Sistema de Gestão Psicológica</p>
          </div>
        </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();
      }
    }
  };

  // =====================================================
  // FORMULÁRIO RESPONSIVO
  // =====================================================
  const FormularioEvolucao = () => (
    <Card sx={{ 
      mb: isMobile ? 2 : 2, 
      borderRadius: 3,
      boxShadow: 3
    }}>
      <CardContent sx={{ p: isMobile ? 2 : 3 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={isMobile ? 3 : 2}>
            {/* Linha com Cliente e Data de Cadastro */}
            <Box sx={{ display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
              <FormControl fullWidth>
                <InputLabel id="cliente-label">Cliente *</InputLabel>
                <Select
                  labelId="cliente-label"
                  value={formData.id_cliente}
                  onChange={(e) => setFormData({ ...formData, id_cliente: Number(e.target.value) })}
                  disabled={saving}
                  label="Cliente *"
                  sx={{
                    minHeight: isMobile ? 56 : 'auto' // Altura mínima para toque
                  }}
                >
                  {Array.isArray(clientes) && clientes.length > 0 ? (
                    clientes.map((cliente) => (
                      <MenuItem key={cliente.id} value={cliente.id}>
                        {cliente.nome}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value={0} disabled>
                      Nenhum cliente ativo encontrado
                    </MenuItem>
                  )}
                </Select>
              </FormControl>

              <TextField
                label="Data de Cadastro"
                type="date"
                value={formData.data_cadastro}
                onChange={(e) => setFormData({ ...formData, data_cadastro: e.target.value })}
                disabled={saving}
                InputLabelProps={{ shrink: true }}
                fullWidth={isMobile}
                sx={{ 
                  minWidth: isMobile ? '100%' : 200,
                  '& .MuiOutlinedInput-root': {
                    minHeight: isMobile ? 56 : 'auto'
                  }
                }}
              />
            </Box>

            <FormControl fullWidth>
              <InputLabel id="tipo-sessao-label">Tipo de Sessão</InputLabel>
              <Select
                labelId="tipo-sessao-label"
                value={formData.tipo_sessao}
                onChange={(e) => setFormData({ ...formData, tipo_sessao: e.target.value })}
                disabled={saving}
                label="Tipo de Sessão"
                sx={{
                  minHeight: isMobile ? 56 : 'auto'
                }}
              >
                <MenuItem value="">Selecione um tipo</MenuItem>
                <MenuItem value="Consulta inicial">Consulta inicial</MenuItem>
                <MenuItem value="Terapia individual">Terapia individual</MenuItem>
                <MenuItem value="Terapia em grupo">Terapia em grupo</MenuItem>
                <MenuItem value="Avaliação Psicológica">Avaliação Psicológica</MenuItem>
                <MenuItem value="Sessão de acompanhamento">Sessão de acompanhamento</MenuItem>
                <MenuItem value="Orientação familiar">Orientação familiar</MenuItem>
                <MenuItem value="Terapia cognitivo-comportamental">Terapia cognitivo-comportamental</MenuItem>
                <MenuItem value="Psicoterapia breve">Psicoterapia breve</MenuItem>
                <MenuItem value="Outros">Outros</MenuItem>
              </Select>
            </FormControl>

            <FormularioTexto
              ref={formularioTextoRef}
              procedimentoInicial={procedimentoValue}
              sinteseInicial={sinteseValue}
              condutaInicial={condutaValue}
              observacoesInicial={observacoesValue}
              onOpenVoiceModal={openVoiceModal}
            />

            <Box sx={{ 
              display: 'flex', 
              justifyContent: isMobile ? 'stretch' : 'flex-end', 
              gap: 2, 
              pt: 2,
              flexDirection: isMobile ? 'column' : 'row'
            }}>
              <Button
                onClick={handleCancel}
                variant="outlined"
                disabled={saving}
                startIcon={<CancelIcon />}
                size={isMobile ? "large" : "medium"}
                sx={{ 
                  minWidth: isMobile ? '100%' : 120,
                  minHeight: isMobile ? 48 : 'auto',
                  order: isMobile ? 2 : 1
                }}
              >
                Cancelar
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                size={isMobile ? "large" : "medium"}
                sx={{ 
                  minWidth: isMobile ? '100%' : 120,
                  minHeight: isMobile ? 48 : 'auto',
                  order: isMobile ? 1 : 2
                }}
              >
                {saving ? 'Salvando...' : (editingId ? 'Atualizar' : 'Salvar')}
              </Button>
            </Box>
          </Stack>
        </form>
      </CardContent>
    </Card>
  );

  // =====================================================
  // CARD DE EVOLUÇÃO RESPONSIVO
  // =====================================================
  const CardEvolucao = ({ evolucao }: { evolucao: Evolucao }) => {
    const isExpanded = expandedCard === evolucao.id;
    
    return (
      <Card sx={{ 
        mb: 2, 
        borderRadius: 3,
        boxShadow: 2,
        '&:hover': { boxShadow: 4 }
      }}>
        <CardContent>
          {/* Cabeçalho do Card */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ color: theme.palette.primary.main, mb: 1 }}>
                <PersonIcon sx={{ mr: 1, fontSize: 20 }} />
                {evolucao.cliente_nome}
              </Typography>
              
              {evolucao.tipo_sessao && (
                <Chip 
                  label={evolucao.tipo_sessao} 
                  size="small" 
                  color="secondary" 
                  sx={{ mb: 1 }}
                />
              )}
              
              <Typography variant="body2" color="text.secondary">
                <CalendarIcon sx={{ mr: 1, fontSize: 16 }} />
                {formatarDataSemFusoHorario(evolucao.data_cadastro)}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => handlePrintEvolucao(evolucao)}
                color="primary"
              >
                <PrintIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleEdit(evolucao)}
                color="primary"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleDelete(evolucao.id)}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Preview do conteúdo */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ 
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              <strong>Procedimento:</strong> {evolucao.procedimento}
            </Typography>
          </Box>

          {/* Botão de expandir */}
          <Button
            onClick={() => toggleExpand(evolucao.id)}
            endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            size="small"
            fullWidth
            variant="outlined"
          >
            {isExpanded ? 'Ver Menos' : 'Ver Detalhes'}
          </Button>

          {/* Conteúdo expandido */}
          <Collapse in={isExpanded}>
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PsychologyIcon sx={{ mr: 1, fontSize: 18 }} />
                    Procedimento
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2">
                      {evolucao.procedimento}
                    </Typography>
                  </Paper>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <DescriptionIcon sx={{ mr: 1, fontSize: 18 }} />
                    Síntese
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2">
                      {evolucao.sintese}
                    </Typography>
                  </Paper>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AssignmentIcon sx={{ mr: 1, fontSize: 18 }} />
                    Conduta
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2">
                      {evolucao.conduta}
                    </Typography>
                  </Paper>
                </Box>

                {evolucao.observacoes && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Observações
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body2">
                        {evolucao.observacoes}
                      </Typography>
                    </Paper>
                  </Box>
                )}
              </Stack>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  };

  // =====================================================
  // RENDER PRINCIPAL
  // =====================================================
  return (
    <Box sx={{ p: isMobile ? 1 : 3 }}>
      {/* Cabeçalho */}
      {showForm ? (
        // Título simples para formulário sem card
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: isMobile ? 2 : 3 }}>
          <IconButton
            onClick={() => {
              // Parar qualquer áudio que esteja tocando ao voltar
              speechSynthesis.cancel();
              
              // Parar reconhecimento de voz se estiver ativo
              if (recognitionRef.current) {
                recognitionRef.current.stop();
              }
              
              navigate(-1);
            }}
            sx={{
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: `${theme.palette.primary.main}15`
              },
              minHeight: 44, // Tamanho mínimo para toque
              minWidth: 44
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          
          <Typography variant={isMobile ? "h6" : "h5"} sx={{ 
            fontWeight: 600, 
            color: theme.palette.primary.main
          }}>
            {editingId ? 'Editar Evolução' : 'Nova Evolução'}
          </Typography>
        </Box>
      ) : (
        // Header completo para listagem
        <Card sx={{ 
          mb: isMobile ? 2 : 3, 
          borderRadius: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
        }}>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Botão Voltar */}
                <IconButton 
                  onClick={() => {
                    // Parar qualquer áudio que esteja tocando ao voltar
                    speechSynthesis.cancel();
                    
                    // Parar reconhecimento de voz se estiver ativo
                    if (recognitionRef.current) {
                      recognitionRef.current.stop();
                    }
                    
                    navigate(-1);
                  }}
                  sx={{
                    color: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.light + '20'
                    }
                  }}
                  size="large"
                >
                  <ArrowBackIcon />
                </IconButton>
                
                <Box>
                  <Typography variant={isMobile ? "h5" : "h4"} sx={{ 
                    fontWeight: 700, 
                    color: theme.palette.primary.main,
                    mb: 1,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    Evoluções
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Gerencie as evoluções dos seus clientes
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexDirection: isMobile ? 'column' : 'row', width: isMobile ? '100%' : 'auto' }}>
                <TextField
                  size={isMobile ? "medium" : "small"}
                  placeholder="Buscar por cliente, procedimento..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
                  }}
                  sx={{ 
                    minWidth: isMobile ? '100%' : 250,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'background.paper',
                      minHeight: isMobile ? 48 : 'auto' // Altura mínima para toque
                    }
                  }}
                />
                
                <Box sx={{ display: 'flex', gap: 1, width: isMobile ? '100%' : 'auto' }}>
                <Button
                    variant={filtrosAplicados.cliente_id || filtrosAplicados.data_inicio || filtrosAplicados.data_fim ? "contained" : "outlined"}
                  startIcon={<FilterListIcon />}
                    onClick={handleOpenFilters}
                    size={isMobile ? "large" : "medium"}
                  sx={{ 
                    borderRadius: 2,
                      px: isMobile ? 2 : 3,
                      py: isMobile ? 1.5 : 1,
                      flex: isMobile ? 1 : 'none',
                      minHeight: isMobile ? 48 : 'auto',
                    '&:hover': { 
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                    {isMobile ? '' : 'Filtros'}
                </Button>
                
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleNew}
                    size={isMobile ? "large" : "medium"}
                  sx={{ 
                    borderRadius: 2,
                      px: isMobile ? 2 : 3,
                      py: isMobile ? 1.5 : 1,
                      flex: isMobile ? 2 : 'none',
                      minHeight: isMobile ? 48 : 'auto',
                    boxShadow: 3,
                    '&:hover': { 
                      boxShadow: 6,
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                    {isMobile ? 'Nova' : 'Nova Evolução'}
                </Button>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}



      {/* Formulário (se estiver visível) */}
      {showForm && <FormularioEvolucao />}

      {/* Lista de evoluções */}
      {!showForm && (
        <>
          {loading ? (
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              </CardContent>
            </Card>
          ) : isMobile ? (
            // Cards responsivos para mobile
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {evolucoesFiltradas.map((evolucao, index) => (
                <Fade in timeout={300 + (index * 100)} key={evolucao.id}>
                  <Card sx={{ 
                    borderRadius: 3,
                    boxShadow: 2,
                    '&:hover': { boxShadow: 4 },
                    transition: 'all 0.2s ease-in-out'
                  }}>
                    <CardContent sx={{ p: 2 }}>
                      {/* Cabeçalho do Card Mobile */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ 
                            color: theme.palette.primary.main, 
                            mb: 0.5,
                            fontSize: '1.1rem',
                            fontWeight: 600
                          }}>
                            <PersonIcon sx={{ mr: 1, fontSize: 18 }} />
                            {evolucao.cliente_nome}
                          </Typography>
                          
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            <CalendarIcon sx={{ mr: 1, fontSize: 16 }} />
                            {formatarDataSemFusoHorario(evolucao.data_cadastro)}
                          </Typography>
                          
                          {evolucao.tipo_sessao && (
                            <Chip 
                              label={evolucao.tipo_sessao} 
                              size="small" 
                              color="secondary" 
                              sx={{ borderRadius: 2, mb: 1 }}
                            />
                          )}
                        </Box>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <IconButton
                            size="medium"
                            onClick={() => handlePrintEvolucao(evolucao)}
                            sx={{ 
                              color: theme.palette.primary.main,
                              minHeight: 44,
                              minWidth: 44,
                              '&:hover': { 
                                backgroundColor: `${theme.palette.primary.main}15`,
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease-in-out'
                            }}
                          >
                            <PrintIcon />
                          </IconButton>
                          <IconButton
                            size="medium"
                            onClick={() => handleEdit(evolucao)}
                            sx={{ 
                              color: theme.palette.primary.main,
                              minHeight: 44,
                              minWidth: 44,
                              '&:hover': { 
                                backgroundColor: `${theme.palette.primary.main}15`,
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease-in-out'
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="medium"
                            onClick={() => handleDelete(evolucao.id)}
                            sx={{ 
                              color: theme.palette.error.main,
                              minHeight: 44,
                              minWidth: 44,
                              '&:hover': { 
                                backgroundColor: `${theme.palette.error.main}15`,
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease-in-out'
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      {/* Conteúdo do procedimento */}
                      <Box>
                        <Typography variant="subtitle2" sx={{ 
                          mb: 1, 
                          color: theme.palette.primary.main,
                          fontWeight: 600
                        }}>
                          <DescriptionIcon sx={{ mr: 1, fontSize: 16 }} />
                          Procedimento
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          lineHeight: 1.4
                        }}>
                          {evolucao.procedimento}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              ))}
              
              {evolucoesFiltradas.length === 0 && (
                <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
                      <PsychologyIcon sx={{ fontSize: 64, color: 'grey.400' }} />
                      <Typography variant="h6" color="text.secondary" textAlign="center">
                        {searchTerm ? 'Nenhuma evolução encontrada' : 'Nenhuma evolução cadastrada'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign="center">
                        {searchTerm ? 'Tente ajustar os termos da busca' : 'Comece criando uma nova evolução'}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>
          ) : (
            // Tabela para desktop
        <Card sx={{ 
          borderRadius: 3,
          boxShadow: 3,
          overflow: 'hidden'
        }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: `${theme.palette.primary.main}08` }}>
                  <TableCell sx={{ fontWeight: 700, color: theme.palette.primary.main }}>Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: theme.palette.primary.main }}>Tipo de Sessão</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: theme.palette.primary.main }}>Data Cadastro</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: theme.palette.primary.main }}>Procedimento</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>Ações</TableCell>
                </TableRow>
              </TableHead>
                <TableBody>
                  {evolucoesFiltradas.map((evolucao, index) => (
                    <Fade in timeout={300 + (index * 100)} key={evolucao.id}>
                      <TableRow 
                        key={evolucao.id} 
                        hover
                        sx={{
                          height: '32px',
                          backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' }
                        }}
                      >
                        <TableCell sx={{ py: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {evolucao.cliente_nome}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          {evolucao.tipo_sessao ? (
                            <Chip
                              label={evolucao.tipo_sessao}
                              size="small"
                              color="secondary"
                              sx={{ borderRadius: 2 }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Não informado
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            {formatarDataSemFusoHorario(evolucao.data_cadastro)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <Typography variant="body2" color="text.secondary" sx={{
                            maxWidth: 200, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {evolucao.procedimento}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Tooltip title="Imprimir">
                              <IconButton
                                size="small"
                                onClick={() => handlePrintEvolucao(evolucao)}
                                sx={{ 
                                  color: theme.palette.primary.main,
                                  '&:hover': { 
                                    backgroundColor: `${theme.palette.primary.main}15`,
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s ease-in-out'
                                }}
                              >
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(evolucao)}
                                sx={{ 
                                  color: theme.palette.primary.main,
                                  '&:hover': { 
                                    backgroundColor: `${theme.palette.primary.main}15`,
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s ease-in-out'
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Excluir">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(evolucao.id)}
                                sx={{ 
                                  color: theme.palette.error.main,
                                  '&:hover': { 
                                    backgroundColor: `${theme.palette.error.main}15`,
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s ease-in-out'
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </Fade>
                  ))}
                    {evolucoesFiltradas.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <PsychologyIcon sx={{ fontSize: 64, color: 'grey.400' }} />
                          <Typography variant="h6" color="text.secondary">
                            {searchTerm ? 'Nenhuma evolução encontrada' : 'Nenhuma evolução cadastrada'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {searchTerm ? 'Tente ajustar os termos da busca' : 'Comece criando uma nova evolução'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
            </Table>
          </TableContainer>
        </Card>
          )}
        </>
      )}



      {/* Modal de Filtros */}
      <Dialog 
        open={showFilters} 
        onClose={handleCloseFilters} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            ...(isMobile && {
              margin: 0,
              width: '100%',
              height: '100%',
              maxHeight: '100%',
              borderRadius: 0
            })
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: isMobile ? '1.1rem' : '1.25rem',
          py: isMobile ? 2 : 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <FilterListIcon />
          Filtros Avançados
        </DialogTitle>
        <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Seleção de Cliente */}
            <FormControl fullWidth>
              <InputLabel id="filtro-cliente-label">Cliente</InputLabel>
              <Select
                labelId="filtro-cliente-label"
                value={filtros.cliente_id}
                onChange={(e) => setFiltros({ ...filtros, cliente_id: e.target.value })}
                label="Cliente"
                sx={{
                  minHeight: isMobile ? 56 : 'auto'
                }}
              >
                <MenuItem value="">
                  <em>Todos os clientes</em>
                </MenuItem>
                {Array.isArray(clientes) && clientes.length > 0 ? (
                  clientes.map((cliente) => (
                    <MenuItem key={cliente.id} value={cliente.id}>
                      {cliente.nome}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>
                    Nenhum cliente encontrado
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {/* Data Inicial */}
            <TextField
              label="Data Inicial"
              type="date"
              value={filtros.data_inicio}
              onChange={(e) => setFiltros({ ...filtros, data_inicio: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
              InputProps={{
                endAdornment: filtros.data_inicio && (
                  <IconButton
                    size="small"
                    onClick={() => setFiltros({ ...filtros, data_inicio: '' })}
                    sx={{ mr: 1 }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )
              }}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  minHeight: isMobile ? 56 : 'auto'
                }
              }}
            />

            {/* Data Final */}
            <TextField
              label="Data Final"
              type="date"
              value={filtros.data_fim}
              onChange={(e) => setFiltros({ ...filtros, data_fim: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
              InputProps={{
                endAdornment: filtros.data_fim && (
                  <IconButton
                    size="small"
                    onClick={() => setFiltros({ ...filtros, data_fim: '' })}
                    sx={{ mr: 1 }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )
              }}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  minHeight: isMobile ? 56 : 'auto'
                }
              }}
            />

            {/* Informação sobre filtros ativos */}
            {(filtrosAplicados.cliente_id || filtrosAplicados.data_inicio || filtrosAplicados.data_fim) && (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Filtros ativos: 
                {filtrosAplicados.cliente_id && (
                  <Chip 
                    label={clientes.find(c => c.id.toString() === filtrosAplicados.cliente_id.toString())?.nome || 'Cliente'} 
                    size="small" 
                    sx={{ ml: 1, mr: 0.5 }} 
                  />
                )}
                {filtrosAplicados.data_inicio && (
                  <Chip 
                    label={`De: ${new Date(filtrosAplicados.data_inicio).toLocaleDateString('pt-BR')}`} 
                    size="small" 
                    sx={{ ml: 0.5, mr: 0.5 }} 
                  />
                )}
                {filtrosAplicados.data_fim && (
                  <Chip 
                    label={`Até: ${new Date(filtrosAplicados.data_fim).toLocaleDateString('pt-BR')}`} 
                    size="small" 
                    sx={{ ml: 0.5 }} 
                  />
                )}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ 
          p: isMobile ? 2 : 3,
          gap: isMobile ? 1 : 0,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Button 
            onClick={handleClearFilters} 
            color="inherit"
            size={isMobile ? "large" : "medium"}
            sx={{
              minHeight: isMobile ? 48 : 'auto',
              width: isMobile ? '100%' : 'auto',
              order: isMobile ? 3 : 1
            }}
          >
            Limpar Filtros
          </Button>
          <Button 
            onClick={handleCloseFilters} 
            color="inherit"
            size={isMobile ? "large" : "medium"}
            sx={{
              minHeight: isMobile ? 48 : 'auto',
              width: isMobile ? '100%' : 'auto',
              order: isMobile ? 2 : 2
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleApplyFilters} 
            variant="contained" 
            color="primary"
            size={isMobile ? "large" : "medium"}
            sx={{
              minHeight: isMobile ? 48 : 'auto',
              width: isMobile ? '100%' : 'auto',
              order: isMobile ? 1 : 3
            }}
          >
            Aplicar Filtros
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Voz */}
      <Dialog 
        open={voiceModal.open} 
        onClose={closeVoiceModal} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            ...(isMobile && {
              margin: 0,
              width: '100%',
              height: '100%',
              maxHeight: '100%',
              borderRadius: 0
            })
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: isMobile ? '1.1rem' : '1.25rem',
          py: isMobile ? 2 : 3
        }}>
          Captura de Voz - {voiceModal.field ? voiceModal.field.charAt(0).toUpperCase() + voiceModal.field.slice(1) : ''}
        </DialogTitle>
        <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
          <Box sx={{ mt: isMobile ? 1 : 2 }}>
            <TextField
              multiline
              rows={isMobile ? 8 : 6}
              value={voiceModal.text}
              onChange={(e) => setVoiceModal(prev => ({ ...prev, text: e.target.value }))}
              placeholder="O texto capturado aparecerá aqui. Você também pode digitar diretamente..."
              fullWidth
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: isMobile ? '16px' : '14px'
                }
              }}
            />
            <Box sx={{ 
              mt: isMobile ? 3 : 2, 
              display: 'flex', 
              gap: 2, 
              justifyContent: 'center',
              flexDirection: isMobile ? 'column' : 'row'
            }}>
              <Button
                variant={voiceModal.isListening ? "contained" : "outlined"}
                color={voiceModal.isListening ? "error" : "primary"}
                onClick={voiceModal.isListening ? stopModalListening : startModalListening}
                startIcon={voiceModal.isListening ? <PulsingStopIcon /> : <MicIcon />}
                size={isMobile ? "large" : "medium"}
                sx={{
                  minHeight: isMobile ? 48 : 'auto',
                  px: isMobile ? 3 : 2
                }}
              >
                {voiceModal.isListening ? 'Parar Gravação' : 'Iniciar Gravação'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: isMobile ? 2 : 3,
          gap: isMobile ? 1 : 0,
          flexDirection: isMobile ? 'column-reverse' : 'row'
        }}>
          <Button 
            onClick={closeVoiceModal} 
            color="inherit"
            size={isMobile ? "large" : "medium"}
            sx={{
              minHeight: isMobile ? 48 : 'auto',
              width: isMobile ? '100%' : 'auto'
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={importVoiceText} 
            variant="contained" 
            color="primary"
            size={isMobile ? "large" : "medium"}
            sx={{
              minHeight: isMobile ? 48 : 'auto',
              width: isMobile ? '100%' : 'auto'
            }}
          >
            Importar Texto
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Evolucoes;
